import React, { type ReactNode } from 'react';
import { useIntl } from 'react-intl-next';

import { Text } from '@atlaskit/primitives/compiled';
import {
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
	SpotlightSecondaryAction,
	SpotlightStepCount,
} from '@atlaskit/spotlight';
import Image from '@atlaskit/image';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';

import homeLight from '../../assets/images/Home.gif';
import homeDark from '../../assets/images/Home-dark.png';
import { spotlight, editorForYouSpotlight } from '../../messages';
import { useTour } from '../../hooks/useNthUserOrientationStore';
import { useSpotlightAnalytics } from '../../hooks/useSpotlightAnalytics';

interface EditorForYouSpotlightProps {
	children: ReactNode;
}
const STEP_ID = 'nth-user-orientation-editor-for-you-spotlight';
export const EditorForYouSpotlight: React.FC<EditorForYouSpotlightProps> = ({ children }) => {
	const { reset, next, back, currentStep, progress, hasInteracted } = useTour(STEP_ID);
	const { formatMessage } = useIntl();
	const isVisible = currentStep === STEP_ID;
	const { PopoverContent } = useChoreographerAssets();

	const { handleNext, handleBack, handleDismiss } = useSpotlightAnalytics({
		spotlightId: STEP_ID,
		isVisible,
		onNext: next,
		onBack: back,
		onDismiss: reset,
	});

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<PopoverContent
				shouldDismissOnClickOutside={false}
				next={handleNext}
				back={handleBack}
				dismiss={handleDismiss}
				placement="right-end"
				offset={[0, 24]}
				isVisible={isVisible}
				messageId={STEP_ID}
				messageType={hasInteracted ? 'transactional' : 'engagement'}
			>
				<SpotlightCard testId={STEP_ID}>
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(editorForYouSpotlight.headline)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightMedia>
						<Image src={homeLight} srcDark={homeDark} alt="" />
					</SpotlightMedia>
					<SpotlightBody>
						<Text>{formatMessage(editorForYouSpotlight.body)}</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightStepCount>{formatMessage(spotlight.progress, progress)}</SpotlightStepCount>
						<SpotlightActions>
							<SpotlightSecondaryAction>{formatMessage(spotlight.back)}</SpotlightSecondaryAction>
							<SpotlightPrimaryAction>{formatMessage(spotlight.next)}</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};
